import React, { useEffect, useState, useRef } from 'react';
import { Navigate } from 'react-router-dom';
import TR from './../../Trans';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import ErrorDialog from '../dialogs/ErrorDialog';
import { GlobalStyles } from "@mui/material";
import backgroundImage from "../../assets/people-group-4-square.jpg";
import { useLocalStorage } from '../../services/useLocalStorage';
import version from './../../version';
import muvikaAttrs from './../../attrs';
import Box from '@mui/material/Box';
import { useNavigate } from "react-router-dom";
import { ReactComponent as MuvikaLogo } from './../../assets/logo.svg';
import { ReactComponent as MuvikaLogoInv } from './../../assets/logo-inv.svg';
import ThemeToggleButton from '../items/ThemeToggleButton';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import IosExternalLinkDialog from '../dialogs/IosExternalLinkDialog';
import CssBaseline from '@mui/material/CssBaseline';
import { Capacitor } from '@capacitor/core';
import TermsDialog from '../dialogs/TermsDialog';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import InputLabel from '@mui/material/InputLabel';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import OutlinedInput from '@mui/material/OutlinedInput';
import IconButton from '@mui/material/IconButton';
import FacebookLoginDialog from '../dialogs/FacebookLoginDialog';
import InputAdornment from '@mui/material/InputAdornment';
import Error from './../items/Error';
import ChooseLanguage from './../items/ChooseLanguage';
import Message from './../items/Message';
import Container from '@mui/material/Container';
import Waiting from '../items/Waiting';
import sessionStore from './../../stores/session';
import messageStore from '../../stores/message';
import environmentStore from './../../stores/environment';
import statusStore from './../../stores/status';
import accountStore from '../../stores/account';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    background: {
      default: "var(--back-color)"
    }
  },
});

function Login() {

  useEffect(() => {
    document.documentElement.style.fontSize = "16px";
    window.dispatchEvent(new Event('refitbuttons'));
    window.dispatchEvent(new Event('resize'));
  }, []);

  const isLoggedIn = sessionStore((state) => state.isLoggedIn);
  const isWaitingLoggedIn = sessionStore((state) => state.isWaitingLoggedIn);
  const account = accountStore((state) => state.account);
  const sessionToken = sessionStore((state) => state.sessionToken);
  const isSessionOpen = sessionStore((state) => state.isSessionOpen);
  const isSessionOpening = sessionStore((state) => state.isSessionOpening);
  const openSession = sessionStore((state) => state.openSession);
  const login = sessionStore((state) => state.login);
  const updateSession = sessionStore((state) => state.updateSession);
  const clearError = messageStore((state) => state.clearError);
  const getEnvironment = environmentStore((state) => state.getEnvironment);
  const environment = environmentStore((state) => state.environment);
  const isLoginSeqError = sessionStore((state) => state.isLoginSeqError);
  const isWaiting = sessionStore((state) => state.isWaiting);
  const setAppStatus = statusStore((state) => state.setAppStatus);
  const logout = sessionStore((state) => state.logout);
  const isWaitingBack = sessionStore((state) => state.isWaitingBack);

  const navigate = useNavigate();

  const [isFacebookLoginDialogOpen, setIsFacebookLoginDialogOpen] = useState(false);

  const handleOpenFacebookLoginDialog = () => {
    setIsFacebookLoginDialogOpen(true);
  };

  const handleCloseFacebookLoginDialog = () => {
    setIsFacebookLoginDialogOpen(false);
  };

  const [isIosExternalLinkDialogOpen, setIsIosExternalLinkDialogOpen] = useState(false);

  const handleOpenIosExternalLinkDialog = () => {
    setIsIosExternalLinkDialogOpen(true);
  };

  const handleCloseIosExternalLinkDialog = () => {
    setIsIosExternalLinkDialogOpen(false);
  };

  const [isSavedUsername, setIsSavedUsername] = useState(false);

  const [errorDialogOpen, setDialogErrorOpen] = useState(true);

  const handleDialogClose = (event, reason) => {
    setDialogErrorOpen(false);
  };

  const [username, setUsername] = useState(() => {
    const saved = localStorage.getItem(muvikaAttrs.service + "-username");
    const initialValue = JSON.parse(saved);
    setIsSavedUsername(initialValue ? true : false);
    return initialValue || "";
  });

  const [password, setPassword] = useState('');
  const [, updateState] = useState();
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem(muvikaAttrs.service + "-session-language");
    const initialValue = JSON.parse(saved);
    return initialValue || "";
  });

  const [showPassword, setShowPassword] = useState(false);

  const [isPrivacyDialogOpen, setIsPrivacyDialogOpen] = useState(false);

  const handleOpenPrivacyDialog = () => {
    setIsPrivacyDialogOpen(true);
  };

  const handleClosePrivacyDialog = () => {
    setIsPrivacyDialogOpen(false);
  };

  useEffect(() => {
    if (!isSessionOpen && !isSessionOpening) {
      console.log("opening session");
      openSession(language.length > 0 ? language.toLowerCase() : "english");
    } else {
      if (language.length > 0) {
        handleLanguageChange(language);
      }
    }
    clearError();
    document.title = TR.ww_page_title;
    setAppStatus(sessionToken, "login");
  }, []);

  useEffect(() => {
    if (!environment) {
      getEnvironment(sessionToken);
    }
  }, [sessionToken]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      localStorage.setItem(muvikaAttrs.service + "-username", JSON.stringify(username));
      login(username, password);
    }
  };

  const handleLanguageChange = (language) => {
    let lowLang = language.toLowerCase();
    setLanguage(lowLang);
    updateSession(sessionToken, lowLang);
    updateState({});
  };

  const handleRegister = () => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      handleOpenIosExternalLinkDialog();
    } else {
      navigate("/startregister");
    }
  };

  const handleFacebook = () => {
    handleOpenFacebookLoginDialog();
  };

  const renderRegisterButton = () => {
    return <Link className="muvika-link-nodec" onClick={handleRegister} variant="body2">
      {TR.cm_register}
    </Link>;
  };

  const [theme, setTheme] = useLocalStorage(muvikaAttrs.service + "-theme", "dark");

  const renderLogoProper = () => {
    if (!theme || theme === "dark") {
      return <MuvikaLogo className="muvika-logo-login-svg" />;
    } else {
      return <MuvikaLogoInv className="muvika-logo-login-svg" />;
    }
  };

  useEffect(() => {
    console.log(theme)
  }, [theme])

  const renderLogo = () => {
    if (Capacitor.isNativePlatform()) {
      return renderLogoProper();
    } else {
      return <a href={language === "finnish" ? muvikaAttrs.linkToWebsiteFi : muvikaAttrs.linkToWebsite}>
        {renderLogoProper()}
      </a>;
    }
  };

  const renderLoginForm = () => {
    return <Box
      className="muvika-entry-box"
    >
      <FacebookLoginDialog
        open={isFacebookLoginDialogOpen}
        onClose={handleCloseFacebookLoginDialog}
      />
      <TermsDialog
        open={isPrivacyDialogOpen}
        onClose={handleClosePrivacyDialog}
        title={TR.cm_register_clause}
        url={environment ? environment.urls.url_privacy_home : ""}
      />
      <IosExternalLinkDialog
        open={isIosExternalLinkDialogOpen}
        onClose={handleCloseIosExternalLinkDialog}
        message={TR.cm_external_link_account_ios}
        url={"https://muvika.com"}
      />
      <ChooseLanguage environment={environment} onLanguageChange={handleLanguageChange} />
      <p>
        {renderLogo()}
      </p>
      <Box component="form" noValidate sx={{ mt: 1 }}>
        <InputLabel>{TR.cm_username}</InputLabel>
        <TextField
          id="login-username"
          size="small"
          required
          fullWidth
          value={username}
          onChange={(e) => setUsername(e.target.value.toLowerCase())}
          autoFocus={!isSavedUsername}
        />
        <InputLabel>{TR.cm_password}</InputLabel>
        <OutlinedInput
          id="login-password"
          autoFocus={isSavedUsername}
          size="small"
          required
          fullWidth
          defaultValue={password}
          onChange={(e) => setPassword(e.target.value)}
          type={showPassword ? 'text' : 'password'}
          endAdornment={
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                onMouseDown={() => setShowPassword(!showPassword)}
                edge="end"
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <Button
          id="muvika-login-submit"
          onClick={() => {
            localStorage.setItem(muvikaAttrs.service + "-username", JSON.stringify(username));
            login(username, password);
          }}
          fullWidth
          variant="text"
          color="success"
          sx={{ mt: 3, mb: 2 }}
        >
          {TR.cm_log_in}
        </Button>
        <Stack direction="row" spacing={6} justifyContent="center" sx={{ textAlign: "center" }}>
          {renderRegisterButton()}
          <Link className="muvika-link-nodec" href="#/forgotpassword" variant="body2">
            {TR.cm_forgot_password}
          </Link>
          {/* <Link className="muvika-link-nodec" onClick={handleFacebook} variant="body2">
            {TR.cm_facebook_login}
          </Link> */}
          {/* <Link className="muvika-link-nodec" style={{ cursor: 'pointer' }} variant="body2" onClick={handleOpenPrivacyDialog}>
            {TR.cm_register_clause}
          </Link> */}
        </Stack>
        <p style={{ textAlign: "center" }}>
          <ThemeToggleButton onClick={updateState} />
        </p>
        {renderVersion()}
      </Box>
    </Box>;
  };

  const renderIfSessionOpen = () => {
    console.log("logon", isLoggedIn, isWaitingLoggedIn);
    if (isWaiting || isWaitingLoggedIn) {
      return <Box
        className="muvika-entry-box"
      >
        <Waiting />
      </Box>;
    } else {
      if (environment && isSessionOpen && !isLoggedIn) {
        return renderLoginForm();
      }
    }
  };

  const renderIfLoggedIn = () => {
    if (isSessionOpen && isLoggedIn) {
      if (isLoginSeqError) {
        return <ErrorDialog
          open={errorDialogOpen}
          onClose={() => {
            sessionStore.getState().reset();
            clearError();
            window.location.reload(false);
            handleDialogClose();
          }}
          title={TR.cm_error}
          message={TR.cm_failed_to_load_all_login_data}
          isRetryable={false}
        />;
      } else {
        if (account) {
          if (account.force_pw_change) {
            return <Navigate to="/forcechangepassword" />;
          } else if (account.force_accept_terms) {
            return <Navigate to="/forceacceptterms" />;
          }
        }
        return <Navigate to="/" />;
      }
    }
  };

  const renderVersion = () => {
    return <p style={{ textAlign: "center" }}><small>{version} {muvikaAttrs.side === "dev" ? "no_rep" : ""}</small></p>;
  };

  const renderToggleTheme = () => {
    return <p style={{ textAlign: "center" }}><ThemeToggleButton /></p>;
  };

  const overflowStyleIos = `
  html {
    overflow-y:scroll !important;
  }
  
  body {
    overflow-y:scroll !important;
  }
  
  main {
    overflow-y:scroll !important;
  }
`;

  const overflowStyle = `
  html {
    overflow-y:auto !important;
  }
  
  body {
    overflow-y:auto !important;
  }
  
  main {
    overflow-y:auto !important;
  }
`;

  const renderOverflowStyle = () => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      return <style>
        {overflowStyleIos}
      </style>;
    } else {
      return <style>
        {overflowStyle}
      </style>;
    }
  };

  return (
    <ThemeProvider theme={darkTheme}>
      {renderOverflowStyle()}
      <GlobalStyles
        styles={{
          body: {
            margin: 0,
            minHeight: "100vh",
            minWidth: "100vw",
            backgroundImage: `url(${backgroundImage}) !important`,
            backgroundRepeat: "no-repeat !important",
            backgroundSize: "cover !important",
            backgroundPosition: "center !important",
          },
        }}
      />
      <Container component="main" className="muvika-entry-container" maxWidth="xs" onKeyDown={handleKeyDown}>
        <CssBaseline />
        <Error />
        <Message />
        {renderIfSessionOpen()}
        {renderIfLoggedIn()}
      </Container>
    </ThemeProvider>
  );
}

export default Login;